import React from 'react';

import { Button, Group, Text } from '@mantine/core';

import styles from './TablePagination.module.scss';

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  const getVisiblePages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const rangeWithDots = [];

    if (currentPage <= 4) {
      for (let i = 1; i <= 6; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push('...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      rangeWithDots.push(1, '...');
      for (let i = totalPages - 5; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
    } else {
      rangeWithDots.push(1, '...');
      for (let i = currentPage - 2; i <= currentPage + 2; i++) {
        rangeWithDots.push(i);
      }
      rangeWithDots.push('...', totalPages);
    }

    return rangeWithDots;
  };

  const handlePageClick = (page: number | string) => {
    if (typeof page === 'number' && page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={styles.paginationContainer}>
      <Group gap={4} className={styles.paginationControls}>
        <Button
          variant="subtle"
          color="gray"
          size="sm"
          className={`${styles.paginationButton} ${styles.navButton}`}
          onClick={handlePrevious}
          disabled={currentPage === 1}>
          &lt;
        </Button>

        <Group gap={2} className={styles.pageNumbers}>
          {visiblePages.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <Text size="sm" c="dimmed" className={styles.ellipsis}>
                  ...
                </Text>
              ) : (
                <Button
                  variant={currentPage === page ? 'filled' : 'subtle'}
                  size="sm"
                  className={`${styles.paginationButton} ${currentPage === page ? styles.activePage : styles.inactivePage}`}
                  onClick={() => handlePageClick(page)}>
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}
        </Group>

        <Button
          variant="subtle"
          color="gray"
          size="sm"
          className={`${styles.paginationButton} ${styles.navButton}`}
          onClick={handleNext}
          disabled={currentPage === totalPages}>
          &gt;
        </Button>
      </Group>
    </div>
  );
};
